import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

const DAY_SECONDS = 24 * 60 * 60;

/** Survives remounts (e.g. main ↔ basket toolbar) so elapsed time keeps counting. */
const persistStore = new Map();

/** Clear persisted elapsed time for a key (e.g. when starting a new puzzle session). */
export function clearPlayElapsedTimerPersist(persistKey) {
    if (persistKey) persistStore.delete(persistKey);
}

/**
 * Format play elapsed seconds for toolbar display.
 * Under 1 hour: MM:SS (e.g. 00:00, 59:59).
 * From 1 hour: H:MM:SS (e.g. 1:00:00).
 * Wraps at 24:00:00 back to 00:00.
 */
export function formatPlayElapsedSeconds(totalSec) {
    const sec = ((Math.floor(totalSec) % DAY_SECONDS) + DAY_SECONDS) % DAY_SECONDS;
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) {
        return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const TIMER_SX = {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    px: 0.75,
    minWidth: "3.75rem",
    justifyContent: "center",
};

const TIMER_TEXT_SX = {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontVariantNumeric: "tabular-nums",
    fontSize: "0.875rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    color: "text.primary",
    lineHeight: 1,
};

function readPersistedAccumulatedMs(persistKey) {
    if (!persistKey) return 0;
    const saved = persistStore.get(persistKey);
    const n = Number(saved?.accumulatedMs);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function writePersistedAccumulatedMs(persistKey, accumulatedMs) {
    if (!persistKey) return;
    persistStore.set(persistKey, { accumulatedMs: Math.max(0, accumulatedMs) });
}

/** Count-up timer for play toolbars; resets display every 24 hours. Supports pause via `running`.
 * Elapsed time is derived from `Date.now()` baselines (not `seconds++`), so pause/resume stays accurate
 * across tab freezes when the parent sets `running={false}` on visibility hide.
 * Pass `persistKey` so remounting (main ↔ basket toolbars) continues the same clock.
 */
export default function PlayElapsedTimer({
    active = true,
    running = true,
    ariaLabel = "Elapsed play time",
    persistKey = null,
    textSx = null,
}) {
    const [display, setDisplay] = useState(() =>
        formatPlayElapsedSeconds(Math.floor(readPersistedAccumulatedMs(persistKey) / 1000)),
    );
    /** Elapsed ms accumulated while previously running (excludes current segment). */
    const accumulatedMsRef = useRef(readPersistedAccumulatedMs(persistKey));
    /** Wall-clock start of the current running segment; null while paused/inactive. */
    const segmentStartMsRef = useRef(null);
    const persistKeyRef = useRef(persistKey);
    persistKeyRef.current = persistKey;

    useEffect(() => {
        // New persist key (different puzzle) — reload baseline.
        accumulatedMsRef.current = readPersistedAccumulatedMs(persistKey);
        segmentStartMsRef.current = null;
        setDisplay(formatPlayElapsedSeconds(Math.floor(accumulatedMsRef.current / 1000)));
    }, [persistKey]);

    useEffect(() => {
        if (!active) {
            accumulatedMsRef.current = 0;
            segmentStartMsRef.current = null;
            if (persistKey) persistStore.delete(persistKey);
            setDisplay("00:00");
            return undefined;
        }

        if (running) {
            if (segmentStartMsRef.current == null) {
                segmentStartMsRef.current = Date.now();
            }
        } else if (segmentStartMsRef.current != null) {
            accumulatedMsRef.current += Date.now() - segmentStartMsRef.current;
            segmentStartMsRef.current = null;
            writePersistedAccumulatedMs(persistKey, accumulatedMsRef.current);
        }

        const readElapsedSec = () => {
            let ms = accumulatedMsRef.current;
            if (segmentStartMsRef.current != null) {
                ms += Date.now() - segmentStartMsRef.current;
            }
            return Math.floor(ms / 1000);
        };

        setDisplay(formatPlayElapsedSeconds(readElapsedSec()));

        if (!running) {
            return undefined;
        }

        const id = setInterval(() => {
            setDisplay(formatPlayElapsedSeconds(readElapsedSec()));
        }, 1000);
        return () => {
            clearInterval(id);
            // Fold open segment into accumulated so the next mount continues.
            if (segmentStartMsRef.current != null) {
                accumulatedMsRef.current += Date.now() - segmentStartMsRef.current;
                segmentStartMsRef.current = null;
            }
            writePersistedAccumulatedMs(persistKeyRef.current, accumulatedMsRef.current);
        };
    }, [active, running, persistKey]);

    if (!active) return null;

    return (
        <Box sx={TIMER_SX} aria-hidden={false}>
            <Typography
                component="span"
                role="timer"
                aria-live="off"
                aria-label={ariaLabel}
                sx={{ ...TIMER_TEXT_SX, ...textSx }}
            >
                {display}
            </Typography>
        </Box>
    );
}

PlayElapsedTimer.propTypes = {
    active: PropTypes.bool,
    running: PropTypes.bool,
    ariaLabel: PropTypes.string,
    persistKey: PropTypes.string,
    /** Override the digits colour when the toolbar sits on a dark backdrop. */
    // eslint-disable-next-line react/forbid-prop-types
    textSx: PropTypes.object,
};
